// ============================================================
// AMMO RAMP - how full a magazine is, as one word.
//
// Exists for the reason healthRamp.ts does: the thresholds were an inline
// ternary in the Armoury grid, and the plate, the hotbar and the attack
// footer would each have grown their own copy. One ramp, one answer.
//
// See dev-docs/code-conventions.md - "Health colour comes from
// engine/healthRamp.ts, never re-derive it." Same rule, different bar.
// ============================================================

export type AmmoBand = 'empty' | 'low' | 'ok';

/** Largest magazine still worth drawing as individual rounds. */
const PIP_LIMIT = 40;

/**
 * How full the magazine is.
 *
 * `max <= 0` means "this weapon does not take ammunition" - a chainsword,
 * not an empty bolter - so it reads `ok`. Reporting it as empty would
 * paint every melee weapon red.
 */
export function ammoBand(current: number, max: number): AmmoBand {
  if (!Number.isFinite(max) || max <= 0) return 'ok';
  if (!Number.isFinite(current)) return 'ok';
  if (current <= 0) return 'empty';
  return current <= Math.floor(max * 0.25) ? 'low' : 'ok';
}

/**
 * Whether a magazine should be drawn as pips rather than a bar.
 *
 * A 28-round bolter magazine reads beautifully as individual rounds. A
 * 250-round heavy bolter belt reads as a smear, so it gets a proportional
 * bar instead. The count in text is shown either way.
 */
export function magazineFits(max: number): boolean {
  return Number.isFinite(max) && max > 0 && max <= PIP_LIMIT;
}

// The ramp's colour stops. These MIRROR the --ammo-* tokens in theme.css -
// an engine module cannot read CSS custom properties, and the convention
// (code-conventions.md) is that data-driven colour ramps computed in the
// engine stay engine-side, the way healthRamp.ts and condColor do.
// GREEN at the top, not gold: on a green-to-red scale gold reads as
// half-depleted, so a full magazine looked already worried (user report,
// v8.195.0). Green-full is also the health ramp's grammar - one visual
// language for "how much of this is left".
const GREEN: readonly number[] = [0x22, 0xc5, 0x5e]; // --ammo-ok
const AMBER: readonly number[] = [0xf5, 0x9e, 0x0b]; // --ammo-low
const RED: readonly number[] = [0xef, 0x44, 0x44]; // --ammo-empty

function mix(a: readonly number[], b: readonly number[], f: number): string {
  const c = a.map((av, i) => Math.round(av + (b[i] - av) * f));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The magazine's colour, GLIDING with how full it is.
 *
 * `ammoBand` steps - grey area, then amber, then red - which meant a
 * magazine looked identical at 100% and 30%, then jumped. This ramp is
 * continuous: green down to 60%, green→amber from 60% to the quarter mark
 * (where it agrees exactly with `ammoBand`'s `low` boundary), amber→red
 * from there to dry. Null for a weapon with no magazine - a chainsword
 * has no fill fraction to speak of.
 */
export function ammoColor(current: number, max: number): string | null {
  if (!Number.isFinite(max) || max <= 0 || !Number.isFinite(current)) return null;
  const t = Math.max(0, Math.min(1, current / max));
  if (t >= 0.6) return mix(GREEN, GREEN, 0);
  if (t >= 0.25) return mix(AMBER, GREEN, (t - 0.25) / 0.35);
  return mix(RED, AMBER, t / 0.25);
}
